import React, { useState } from "react";
import "./PGAModelBuilderFeatureOutput.css";
import { graphql, useLazyLoadQuery } from "react-relay";
import StatTable from "../../../../../../../StatTable";
import PlayerInfoDisplay from "../../../../LiveTournamentToolPage/LiveScorecards/PlayerInfoDisplay";
import { sortGolfToPars, sortModelValues } from "../../../../../../../../Utils";
import DraftkingsInfoOverlay from "../../../../../../../DraftKingsInfoOverlay";

const GET_OUTPUT = graphql`
  query PGAModelBuilderFeatureOutputQuery($featureID: ID!) {
    outputForCalcFeature(featureId: $featureID) {
      player {
        id
        nameShort
        firstName
        lastName
        nameDisplay
        dkPlayer {
          id
        }
      }

      num
      stat
      diff
      zscore
      modelScore
      orderedTeeTimesUsed {
        eventPlayer {
          event {
            pgaTourId
            season {
              queryValue
            }
            tournament {
              officialName
            }
          }
        }

        roundNumber
      }
    }
  }
`;

export default function PGAModelBuilderFeatureOutput(props) {
  const queryData = useLazyLoadQuery(
    GET_OUTPUT,
    {
      featureID: props.featureID,
    },
    { fetchKey: props.featureOutputReloads, fetchPolicy: "network-only" }
  );

  const [selectedDkPlayerId, setSelectedDkPlayerId] = useState(undefined);

  let data = React.useMemo(() => {
    if (!queryData.outputForCalcFeature) {
      return null;
    }

    return queryData.outputForCalcFeature.map((row) => {
      const newObj = { ...row };

      newObj.player = row.player.firstName + " " + row.player.lastName;

      if (row.player.dkPlayer) {
        newObj.dkPlayerId = row.player.dkPlayer.id;
      }

      if (row.num === null) {
        newObj.num = "-";
      }

      if (row.stat === null) {
        newObj.stat = "-";
      }

      if (row.diff === null) {
        newObj.diff = "-";
      }

      if (row.zscore === null) {
        newObj.zscore = "-";
      }

      if (row.modelScore === null) {
        newObj.modelScore = "-";
      }

      return newObj;
    });
  }, [props.featureOutputReloads]);

  if (!data) {
    return <div>Build model to see output.</div>;
  }

  let columns = [
    {
      Header: "Name",
      accessor: "player",
    },
    {
      Header: "Num",
      accessor: "num",
      sortType: sortModelValues,
    },
    {
      Header: "Stat",
      accessor: "stat",
      sortType: sortModelValues,
    },
    {
      Header: "Diff",
      accessor: "diff",
      sortType: sortModelValues,
    },
    {
      Header: "Z-Score",
      accessor: "zscore",
      sortType: sortModelValues,
    },
    {
      Header: "Model Score",
      accessor: "modelScore",
      sortType: sortModelValues,
    },
  ];

  const renderPlayerDrawer = React.useCallback(({ row }) => {
    return (
      <div className="feature-output-drawer">
        {row.original.orderedTeeTimesUsed.map((tee_time, index) => {
          return (
            <span key={index}>
              <a
                href={`https://www.pgatour.com/tournaments/${tee_time.eventPlayer.event.season.queryValue}/x/${tee_time.eventPlayer.event.pgaTourId}/leaderboard`}
                target="_blank"
                rel="noreferrer"
              >
                {tee_time.eventPlayer.event.tournament.officialName} (Round{" "}
                {tee_time.roundNumber})
              </a>
            </span>
          );
        })}
      </div>
    );
  }, []);

  function showDKPlayerInfo(dkPlayerId) {
    if (dkPlayerId) {
      setSelectedDkPlayerId(dkPlayerId);
    }
  }

  function hideDKPlayerInfo() {
    setSelectedDkPlayerId(undefined);
  }

  const tableMemo = React.useMemo(() => {
    return (
      <StatTable
        data={data}
        columns={columns}
        showDKPlayerInfo={showDKPlayerInfo}
        // renderRowSubComponent={renderPlayerDrawer} TODO: Tee times need to be added in backend, currently broken
        paginate={true}
      />
    );
  }, [props.featureOutputReloads]);

  return (
    <div style={{ width: "100%" }}>
      <DraftkingsInfoOverlay
        show={selectedDkPlayerId !== undefined}
        playerId={selectedDkPlayerId}
        close={hideDKPlayerInfo}
      />
      {tableMemo}
    </div>
  );
}
